'use client';

import React, { useCallback, useEffect, useId, useRef } from 'react';
import { cn } from '@ncai-axe/react-ui-v3';
import styles from './styles.module.scss';
import type { ConfirmProps } from './types';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(', ');

export default function Confirm({
  width = '320',
  title,
  content,
  cancelButtonText,
  actionButtonText,
  onCancel,
  onAction,
  onClose,
  isOpen = false,
  hideOnClickOutside,
  className,
}: ConfirmProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }

      if (e.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;

      const focusableElements =
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!hideOnClickOutside) return;
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div
      className={cn(styles.backdrop, className)}
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        className={`${styles.modal} ${styles[`width-${width}`]}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={content ? `${titleId}-desc` : undefined}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.labels}>
          {title && (
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
          )}
          {content && (
            <p id={`${titleId}-desc`} className={styles.content}>
              {content}
            </p>
          )}
        </div>

        {(cancelButtonText || actionButtonText) && (
          <div className={styles.buttons}>
            {cancelButtonText && (
              <button
                type="button"
                onClick={onCancel}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                {cancelButtonText}
              </button>
            )}
            {actionButtonText && (
              <button
                type="button"
                onClick={onAction}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                {actionButtonText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
